import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabaseClient } from "../../lib/supabase";
import { LoadingScreen } from "../common/LoadingScreen";
import { Edit, Trash, ArrowLeft, Globe, BarChart2, PenSquare, RefreshCw, Database } from 'lucide-react';
import { parseCompetitorContent } from "../../lib/content-parser";
import { createSerpTask, analyzeSerpResults } from "../../lib/dataforseo";
import ContentAnalysisTab from "./ContentAnalysisTab";
import TextAnalysisTab from "./TextAnalysisTab";
import { addToQueue } from "../../lib/analysis-queue";
import { toast } from "../../lib/toast";

interface Analysis {
  id: string;
  name: string;
  keyword: string;
  quick_score?: number;
  status: 'pending' | 'processing' | 'completed' | 'error';
  created_at: string;
  serp_results?: any[];
  content_analysis?: any;
  url?: string;
  editor_content?: string;
  text_score?: number | null;
}

interface TabProps {
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function Tab({ isActive, onClick, children }: TabProps) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 font-medium text-sm rounded-md ${
        isActive
          ? "bg-indigo-100 text-indigo-700"
          : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

function calculateTextScore(editorContent: string, contentAnalysis: any): number {
  try {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = editorContent;
    
    const text = tempDiv.textContent || '';
    const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
    
    const competitors = contentAnalysis.competitors || [];
    const selectedDomains = contentAnalysis.selectedDomains || [];
    
    const selectedCompetitors = competitors.filter((comp: any) => 
      comp && comp.domain && selectedDomains.includes(comp.domain)
    );
    
    if (selectedCompetitors.length === 0) {
      return 0;
    }
    
    const avgWordCount = Math.round(
      selectedCompetitors.reduce((sum: number, comp: any) => sum + (comp.word_count || 0), 0) / 
      selectedCompetitors.length
    );
    
    return Math.min(100, Math.round((wordCount / Math.max(1, avgWordCount)) * 100));
  } catch (err) {
    console.error('Error calculating text score:', err);
    return 0;
  }
}

export function AnalysisShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'serp' | 'content' | 'text'>('serp');
  const [parsingStatus, setParsingStatus] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isRefreshingSERP, setIsRefreshingSERP] = useState(false);
  const [isAddingToQueue, setIsAddingToQueue] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const subscription = supabaseClient
      .channel('analysis-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'analyses',
          filter: `id=eq.${id}`
        },
        (payload) => {
          setAnalysis(current => 
            current ? { ...current, ...payload.new } : null
          );
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [id]);

  useEffect(() => {
    const loadAnalysis = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabaseClient
          .from("analyses")
          .select("*, project_id")
          .eq("id", id)
          .single();

        if (error) throw error;
        
        // Set project_id for navigation
        setProjectId(data.project_id || null);
        
        // Calculate text score if needed
        if (data.editor_content && data.content_analysis?.competitors) {
          if (data.text_score === null || data.text_score === undefined) {
            const score = calculateTextScore(data.editor_content, data.content_analysis);
            
            const { error: updateError } = await supabaseClient
              .from("analyses")
              .update({ text_score: score })
              .eq("id", id);

            if (updateError) {
              console.error('Error updating text score:', updateError);
            }

            data.text_score = score;
          }
        }

        setAnalysis(data);
      } catch (err) {
        console.error("Error loading analysis:", err);
        setError(err instanceof Error ? err.message : "Failed to load analysis");
      } finally {
        setLoading(false);
      }
    };

    loadAnalysis();
  }, [id]);

  const handleRefreshSERP = async () => {
    if (!analysis) return;

    setIsRefreshingSERP(true);
    setError(null);

    try {
      const results = await createSerpTask(analysis.keyword, "2804"); // Default to Ukraine
      const score = await analyzeSerpResults(results);

      const { error: updateError } = await supabaseClient
        .from("analyses")
        .update({ 
          serp_results: results,
          quick_score: score,
          status: 'completed'
        })
        .eq("id", analysis.id);

      if (updateError) throw updateError;

      setAnalysis({
        ...analysis,
        serp_results: results,
        quick_score: score,
        status: 'completed'
      });

      setError(null);
    } catch (err) {
      console.error("Error refreshing SERP:", err);
      setError(err instanceof Error ? err.message : "Failed to refresh SERP results");
    } finally {
      setIsRefreshingSERP(false);
    }
  };

  const handleAddToQueue = async () => {
    if (!analysis) return;
    
    setIsAddingToQueue(true);
    
    try {
      const result = await addToQueue(analysis.id);
      
      if (result) {
        toast.success('Analysis added to processing queue');
      }
    } catch (err) {
      console.error('Error adding to queue:', err);
      toast.error('Failed to add to processing queue');
    } finally {
      setIsAddingToQueue(false);
    }
  };

  const handleRefreshAnalysis = async () => {
    if (!analysis) return;

    setIsParsing(true);
    setError(null);

    try {
      const contentAnalysis = await parseCompetitorContent(
        analysis.serp_results || [],
        analysis.url || null,
        (progress) => setParsingStatus(progress)
      );

      const { error: updateError } = await supabaseClient
        .from("analyses")
        .update({ content_analysis: contentAnalysis })
        .eq("id", analysis.id);

      if (updateError) throw updateError;

      const updatedAnalysis = {
        ...analysis,
        content_analysis: contentAnalysis
      };
      
      setAnalysis(updatedAnalysis);
      
      // Recalculate text score if editor content exists
      if (analysis.editor_content) {
        const score = calculateTextScore(analysis.editor_content, contentAnalysis);
        
        const { error: scoreUpdateError } = await supabaseClient
          .from("analyses")
          .update({ text_score: score })
          .eq("id", analysis.id);

        if (scoreUpdateError) {
          console.error('Error updating text score:', scoreUpdateError);
        }

        updatedAnalysis.text_score = score;
        setAnalysis(updatedAnalysis);
      }

      setParsingStatus("Analysis completed successfully!");
    } catch (err) {
      console.error("Error refreshing analysis:", err);
      setError(err instanceof Error ? err.message : "Failed to refresh analysis");
    } finally {
      setIsParsing(false);
      setParsingStatus(null);
    }
  };

  const getTextScoreColor = (score: number | null | undefined) => {
    if (score === null || score === undefined) return 'bg-gray-100 text-gray-800';
    if (score >= 75) return 'bg-green-100 text-green-800';
    if (score >= 50) return 'bg-blue-100 text-blue-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const getTextScoreDisplay = (score: number | null | undefined) => {
    if (score === null || score === undefined) return 'N/A';
    return `${score}%`;
  };

  if (loading) return <LoadingScreen />;
  if (!analysis) return <div>Analysis not found</div>;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-8">
            <h2 className="text-xl font-semibold text-gray-800">
              {analysis.name}
            </h2>
            <div>
              <span className="text-sm text-gray-500">Keyword:</span>
              <span className="ml-2 text-sm font-medium text-gray-900">
                {analysis.keyword}
              </span>
            </div>
            <div>
              <span className="text-sm text-gray-500">Text Score:</span>
              <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getTextScoreColor(analysis.text_score)}`}>
                {getTextScoreDisplay(analysis.text_score)}
              </span>
            </div>
            <div>
              <span className="text-sm text-gray-500">Status:</span>
              <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                analysis.status === 'completed' ? 'bg-green-100 text-green-800' :
                analysis.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                analysis.status === 'error' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {analysis.status}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAddToQueue}
              disabled={isAddingToQueue}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <Database className={`h-4 w-4 mr-2 ${isAddingToQueue ? 'animate-pulse' : ''}`} />
              {isAddingToQueue ? 'Adding...' : 'Add to Queue'}
            </button>
            <button
              onClick={() => navigate(`/analyses/${id}/editor`)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PenSquare className="h-4 w-4 mr-2" />
              Editor
            </button>
            <button
              onClick={() => {
                if (projectId) {
                  navigate(`/projects/show/${projectId}`);
                } else {
                  navigate(-1);
                }
              }}
              type="button"
              aria-label="Go back to project"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <div className="flex space-x-4">
            <Tab
              isActive={activeTab === 'serp'}
              onClick={() => setActiveTab('serp')}
            >
              <div className="flex items-center space-x-2">
                <BarChart2 className="h-4 w-4" />
                <span>SERP</span>
              </div>
            </Tab>
            <Tab
              isActive={activeTab === 'content'}
              onClick={() => setActiveTab('content')}
            >
              <div className="flex items-center space-x-2">
                <Edit className="h-4 w-4" />
                <span>Content</span>
              </div>
            </Tab>
            <Tab
              isActive={activeTab === 'text'}
              onClick={() => setActiveTab('text')}
            >
              <div className="flex items-center space-x-2">
                <BarChart2 className="h-4 w-4" />
                <span>Keywords</span>
              </div>
            </Tab>
          </div>
        </div>

        {activeTab === 'serp' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">TOP-10 Results</h3>
              <div className="flex gap-2">
                <button
                  onClick={handleAddToQueue}
                  disabled={isAddingToQueue}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  <Database className={`h-4 w-4 mr-2 ${isAddingToQueue ? 'animate-pulse' : ''}`} />
                  {isAddingToQueue ? 'Adding...' : 'Add to Queue'}
                </button>
                <button
                  onClick={handleRefreshSERP}
                  disabled={isRefreshingSERP}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshingSERP ? 'animate-spin' : ''}`} />
                  {isRefreshingSERP ? 'Refreshing...' : 'Refresh SERP'}
                </button>
              </div>
            </div>

            {analysis.serp_results && analysis.serp_results.length > 0 ? (
              analysis.serp_results.map((result, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold text-gray-500">#{result.position}</span>
                        <h4 className="text-lg font-medium text-blue-600 hover:text-blue-800">
                          <a href={result.url} target="_blank" rel="noopener noreferrer">
                            {result.title}
                          </a>
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{result.description}</p>
                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Globe className="h-4 w-4 mr-1" />
                          {result.domain}
                        </div>
                        <div>
                          Links: {result.links_count || 0}
                        </div>
                        {result.estimated_paid_traffic_cost > 0 && (
                          <div className="text-green-600">
                            Est. Traffic Cost: ${result.estimated_paid_traffic_cost}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No SERP results available for this analysis.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'content' && (
          <div>
            {parsingStatus && (
              <div className="mb-6 bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2" />
                  <p className="text-sm text-blue-700">{parsingStatus}</p>
                </div>
              </div>
            )}

            <ContentAnalysisTab 
              analysisId={id!} 
              contentAnalysis={analysis.content_analysis}
              onRefresh={handleRefreshAnalysis}
            />
          </div>
        )}

        {activeTab === 'text' && (
          <TextAnalysisTab
            analysisId={id!}
            contentAnalysis={analysis.content_analysis}
          />
        )}
      </div>
    </div>
  );
}